<template>
  <div>
    <b-overlay
      :show="recipeGroupPending(data.item._id) && recipeGroupRecipesEmpty(data.item._id)"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <b-card
        no-body
        no-header
        class="card-custom card-custom card-border recipe-card">
        <b-card-body class="px-6 pb-6">
          <b-table
            :items="data.item.recipes_list"
            :fields="fields"
            :class="{ 'table-hover' : !recipeGroupRecipesEmpty(data.item._id) }"
            stacked="lg"
            responsive
            show-empty
            small
            thead-class="small no-events"
            head-variant="white">
            <template #cell(order)="row">
              <span class="small font-weight-light">{{ row.index + 1 }}</span>
            </template>
            <template #cell(shift)="row">
              <b-button-group size="sm">
                <b-button
                  variant="link-primary"
                  v-wave
                  :disabled="row.index == 0"
                  class="align-middle rounded"
                  @click="changeRecipeOrder(row.item._id, 'up')">
                  <i class="fa fa-arrow-up fa-sm m-0 p-2 opacity-75"></i>
                </b-button>
                <b-button
                  variant="link-primary"
                  v-wave
                  :disabled="row.index + 1 === recipesListLength"
                  class="align-middle rounded"
                  @click="changeRecipeOrder(row.item._id, 'down')">
                  <i class="fa fa-arrow-down fa-sm m-0 p-2 opacity-75"></i>
                </b-button>
              </b-button-group>
            </template>
            <template #cell(name)="row">
              <div class="d-flex align-items-center">
                <span
                  class="d-inline-block text-truncate"
                  style="max-width: 220px;">{{ row.item.name }}</span>
                <span
                  v-if="row.item.description"
                  v-b-tooltip.hover.right
                  :title="row.item.description"
                  class="label label-sm ml-3 cursor-help">
                  <i class="fas fa-question fa-xs"></i>
                </span>
              </div>
            </template>
            <template #cell(version)="row">
              <div v-if="row.item.version">
                <span class="font-size-xs">v</span>
                <span>{{ row.item.version }}</span>
              </div>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(tags)="row">
              <div
                v-if="row.item.tags && row.item.tags.length > 0"
                class="d-flex flex-wrap w-100">
                <b-badge
                  v-for="tag in row.item.tags"
                  :key="`tag-${tag}-${row.item._id}`"
                  variant="light-info"
                  class="mr-2 my-1">{{ tag }}</b-badge>
              </div>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(edit)="row">
              <b-button
                :id="`deleteButton-${data.item._id}-${row.item._id}`"
                size="sm"
                variant="link-primary"
                class="align-middle"
                :disabled="recipeGroupsResponses('deleteRecipeGroupRecipe')"
                @click="
                  unselectRecipeGroup(),
                  selectRecipeGroup(data.item),
                  selectRecipeGroupRecipe(row.item),
                  showDeletePopover(`${data.item._id}-${row.item._id}`)
                ">
                <i class="fa fa-trash fa-sm"></i>
                <span>Delete</span>
              </b-button>
            </template>
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>
          <b-button
            size="sm"
            variant="outline-primary"
            class="align-middle"
            :disabled="recipesResponses('recipesList') || recipeGroupsResponses('deleteRecipeGroupRecipe')"
            @click="
              unselectRecipeGroup(),
              selectRecipeGroup(data.item),
              showModal('newRecipeModal')
            ">
            <i class="fa fa-plus fa-sm p-0 p-sm-1"></i>
            <span class="d-none d-sm-inline">Add New Recipe</span>
          </b-button>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-popover
      v-for="group in data.item.recipes_list"
      :key="`popover-${data.item._id}-${group._id}`"
      :target="`deleteButton-${data.item._id}-${group._id}`"
      :ref="`popover-${data.item._id}-${group._id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete recipe «<span class="font-weight-bolder">{{ group.name }}</span>» from «<span class="font-weight-bolder">{{ selectedRecipeGroup.name }}</span>» recipe group?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(`${data.item._id}-${group._id}`)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="
            deleteRecipeGroupRecipe(),
            hideDeletePopover(`${data.item._id}-${group._id}`)
          ">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>
  </div>
</template>

<script>
import {
  // actions
  GET_RECIPES
} from '@/core/services/store/recipes.module'
import {
  // actions
  GET_RECIPE_GROUP,
  GET_RECIPE_GROUP_RECIPES,
  SORT_RECIPE_GROUP_RECIPES,
  DELETE_RECIPE_GROUP_RECIPE,

  // mutations
  SAVE_RECIPE_GROUP_RECIPE,
  CLEAR_RECIPE_GROUP_RECIPE,
  SELECT_RECIPE_GROUP,
  UNSELECT_RECIPE_GROUP,
} from '@/core/services/store/recipes-groups.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { showDeletePopover, hideDeletePopover } from '@/core/utils'

export default {
  name: 'RecipesGroupsDetails',
  props: {
    data: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      fields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;'
        },
        {
          label: '',
          key: 'shift',
          class: ['align-middle', 'px-0'],
          thStyle: 'width: 100px;'
        },
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'text-nowrap', 'px-4'],
          thStyle: 'width: 25%;'
        },
        {
          label: 'Script Version',
          key: 'version',
          class: ['align-middle', 'text-nowrap'],
          thStyle: 'padding-bottom: 1rem !important;'
        },
        {
          label: 'Tags',
          key: 'tags',
          class: ['align-middle', 'text-nowrap', 'font-size-sm'],
          thStyle: 'width: 30%;'
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-lg-right', 'text-nowrap', 'pr-5'],
          thStyle: 'padding-bottom: 1rem !important;'
        }
      ],

      showDeletePopover,
      hideDeletePopover,
    }
  },
  computed: {
    ...mapGetters([
      'recipesGroupsList',
      'recipesGroupsEmpty',
      'totalRecipesGroups',
      'selectedRecipeGroup',
      'selectedRecipeGroupsExist',
      'recipeGroupPending',
      'recipeGroupRecipes',
      'recipeGroupRecipesEmpty',
      'recipeGroupRecipeHasDescription',
      'recipesGroupsModalTitle',
      'manageRecipeGroupErrors',
      'manageRecipeGroupError',
      'recipeGroupsResponses',
      'recipesResponses',
    ]),

    recipesListLength() {
      if (this.data.item.recipes_list) {
        return this.data.item.recipes_list.length
      } else {
        return 0
      }
    },
  },
  methods: {
    changeRecipeOrder(id, direction) {
      // Compose an array with current items order
      const order = this.data.item.recipes_list.map(element => element._id)

      // Get index of the passed ID in that array
      const index = order.indexOf(id)

      // Create a copy of the order array to avoid modifying the original data
      const modifiedOrder = [...order]

      // Modify the order in the copy
      if (direction === 'up' && index > 0) {
        // Move the item up by swapping it with the previous item
        const temp = modifiedOrder[index]
        modifiedOrder[index] = modifiedOrder[index - 1]
        modifiedOrder[index - 1] = temp
      }
      else if (direction === 'down' && index < modifiedOrder.length - 1) {
        // Move the item down by swapping it with the next item
        const temp = modifiedOrder[index]
        modifiedOrder[index] = modifiedOrder[index + 1]
        modifiedOrder[index + 1] = temp
      }

      // Send the modified order payload to the server
      this.sortRecipeGroupRecipes(modifiedOrder)
    },

    selectRecipeGroup(group) {
      this.$store.commit(SELECT_RECIPE_GROUP, group)
    },

    unselectRecipeGroup() {
      this.$store.commit(UNSELECT_RECIPE_GROUP)
    },

    selectRecipeGroupRecipe(version) {
      this.$store.commit(SAVE_RECIPE_GROUP_RECIPE, version)
    },

    unselectRecipeGroupRecipe() {
      this.$store.commit(CLEAR_RECIPE_GROUP_RECIPE)
    },

    getRecipeGroup(id) {
      this.$store.dispatch(GET_RECIPE_GROUP, { id })
    },

    getRecipeGroupRecipes(id, isShowing) {
      this.$store.dispatch(GET_RECIPE_GROUP_RECIPES, { id, isShowing })
    },

    sortRecipeGroupRecipes(order) {
      this.$store
        .dispatch(SORT_RECIPE_GROUP_RECIPES, {
          id: this.data.item._id,
          order
        })
        .then(() => {
          this.getRecipeGroup(this.data.item._id)
          this.getRecipeGroupRecipes(this.data.item._id, false)
        })
    },

    deleteRecipeGroupRecipe() {
      this.$store
        .dispatch(DELETE_RECIPE_GROUP_RECIPE)
        .then(() => {
          this.getRecipeGroup(this.data.item._id)
          this.getRecipeGroupRecipes(this.data.item._id, false)
        })
    },

    showModal(type) {
      this.$store
        .dispatch(GET_RECIPES)
        .then(() => { eventBus.$emit('openModal', type) })
    }
  }
}
</script>
